import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminNavbar from '../../component/adminNavbar';
import {
	Box,
	Container,
	Heading,
	Text,
	Flex,
	Card,
	CardBody,
	Badge,
	Spinner,
	Center,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Button,
	HStack,
	Icon,
	SimpleGrid,
	useToast,
} from '@chakra-ui/react';
import { MdSync, MdChevronLeft, MdChevronRight, MdCable, MdCheckCircle, MdError } from 'react-icons/md';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const PAGE_SIZE = 50;

const authHeaders = () => ({
	headers: { 'x-auth-token': localStorage.getItem('x-auth-token') },
});

const formatWhen = (value?: string | null) => {
	if (!value) return '—';
	return new Date(value).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const statusColor = (status?: string) => {
	const s = (status || '').toLowerCase();
	if (s === 'success') return 'green';
	if (s === 'error') return 'red';
	if (s === 'running') return 'blue';
	return 'gray';
};

const Fishbowl = () => {
	const toast = useToast();
	const [runs, setRuns] = useState<any[]>([]);
	const [count, setCount] = useState(0);
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);
	const [status, setStatus] = useState<any>(null);
	const [busy, setBusy] = useState<'test' | 'sync' | 'uoms' | null>(null);

	const loadStatus = useCallback(async () => {
		try {
			const res = await axios.get(`${BASE_URL}/api/fishbowl/status`, authHeaders());
			setStatus(res.data);
		} catch (e) {
			console.error('Failed to load Fishbowl status', e);
		}
	}, []);

	const loadRuns = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axios.get(
				`${BASE_URL}/api/fishbowl/sync-runs?offset=${offset}&limit=${PAGE_SIZE}`,
				authHeaders()
			);
			setRuns(res.data.data || []);
			setCount(res.data.count || 0);
		} catch (e) {
			console.error('Failed to load sync runs', e);
		} finally {
			setLoading(false);
		}
	}, [offset]);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	useEffect(() => {
		loadRuns();
	}, [loadRuns]);

	const handleTestConnection = async () => {
		setBusy('test');
		try {
			const res = await axios.post(`${BASE_URL}/api/fishbowl/test-connection`, {}, authHeaders());
			toast({
				title: 'Connection OK',
				description: res.data?.message || 'Fishbowl responded successfully.',
				status: 'success',
				duration: 4000,
				isClosable: true,
			});
		} catch (e: any) {
			toast({
				title: 'Connection failed',
				description: e?.response?.data?.error || e.message || 'Could not reach Fishbowl.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setBusy(null);
		}
	};

	const handleSyncNow = async () => {
		setBusy('sync');
		try {
			const res = await axios.post(`${BASE_URL}/api/fishbowl/sync-inventory`, {}, authHeaders());
			toast({
				title: 'Inventory sync complete',
				description: res.data?.message || `Updated ${res.data?.updated ?? 0} items.`,
				status: 'success',
				duration: 4000,
				isClosable: true,
			});
			await loadStatus();
			if (offset === 0) {
				await loadRuns();
			} else {
				setOffset(0);
			}
		} catch (e: any) {
			toast({
				title: 'Sync failed',
				description: e?.response?.data?.error || e.message || 'Inventory sync failed.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			await loadStatus();
			if (offset === 0) {
				await loadRuns();
			} else {
				setOffset(0);
			}
		} finally {
			setBusy(null);
		}
	};

	const handleSyncUoms = async () => {
		setBusy('uoms');
		try {
			const res = await axios.post(`${BASE_URL}/api/fishbowl/sync-uoms`, {}, authHeaders());
			toast({
				title: 'UOM sync complete',
				description: res.data?.message || `Synced ${res.data?.updated ?? 0} UOMs.`,
				status: 'success',
				duration: 4000,
				isClosable: true,
			});
			await loadStatus();
			if (offset === 0) {
				await loadRuns();
			} else {
				setOffset(0);
			}
		} catch (e: any) {
			toast({
				title: 'UOM sync failed',
				description: e?.response?.data?.error || e.message || 'UOM sync failed.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			await loadStatus();
			if (offset === 0) {
				await loadRuns();
			} else {
				setOffset(0);
			}
		} finally {
			setBusy(null);
		}
	};

	const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
	const lastSync = status?.lastSync;
	const lastUomSync = status?.lastUomSync;

	return (
		<>
			<AdminNavbar />
			<Box ml={{ base: 0, md: '250px' }} bg="gray.50" minH="100vh" pt={{ base: 16, md: 0 }}>
				<Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
					<Flex align="center" justify="space-between" gap={3} mb={6} flexWrap="wrap">
						<Flex align="center" gap={3}>
							<Flex w="44px" h="44px" borderRadius="xl" bg="blue.50" align="center" justify="center">
								<Icon as={MdSync} boxSize={6} color="blue.500" />
							</Flex>
							<Box>
								<Heading size="lg" color="gray.800">Fishbowl Sync</Heading>
								<Text fontSize="sm" color="gray.400">{count} sync runs</Text>
							</Box>
						</Flex>
						<HStack spacing={2}>
							<Button
								size="sm"
								variant="outline"
								leftIcon={<Icon as={MdCable} />}
								onClick={handleTestConnection}
								isLoading={busy === 'test'}
								isDisabled={busy !== null}
							>
								Test connection
							</Button>
							<Button
								size="sm"
								variant="outline"
								colorScheme="purple"
								leftIcon={<Icon as={MdSync} />}
								onClick={handleSyncUoms}
								isLoading={busy === 'uoms'}
								isDisabled={busy !== null || status?.configured === false}
							>
								Sync UOMs
							</Button>
							<Button
								size="sm"
								colorScheme="blue"
								leftIcon={<Icon as={MdSync} />}
								onClick={handleSyncNow}
								isLoading={busy === 'sync'}
								isDisabled={busy !== null || status?.configured === false}
							>
								Sync inventory
							</Button>
						</HStack>
					</Flex>

					<SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
						<Card borderRadius="xl" shadow="sm">
							<CardBody>
								<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
									Configured
								</Text>
								<HStack>
									<Icon
										as={status?.configured ? MdCheckCircle : MdError}
										color={status?.configured ? 'green.500' : 'red.400'}
									/>
									<Text fontWeight="semibold" color="gray.800">
										{status == null ? '…' : status.configured ? 'Yes' : 'No'}
									</Text>
								</HStack>
							</CardBody>
						</Card>
						<Card borderRadius="xl" shadow="sm">
							<CardBody>
								<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
									Auto sync
								</Text>
								<Text fontWeight="semibold" color="gray.800">
									{status == null ? '…' : status.syncEnabled ? 'Enabled' : 'Disabled'}
								</Text>
							</CardBody>
						</Card>
						<Card borderRadius="xl" shadow="sm">
							<CardBody>
								<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
									Last inventory sync
								</Text>
								<Text fontWeight="semibold" color="gray.800" fontSize="sm">
									{formatWhen(lastSync?.finished_at || lastSync?.started_at)}
								</Text>
								{lastSync?.status && (
									<Badge mt={1} colorScheme={statusColor(lastSync.status)} variant="subtle" borderRadius="full">
										{lastSync.status}
									</Badge>
								)}
							</CardBody>
						</Card>
						<Card borderRadius="xl" shadow="sm">
							<CardBody>
								<Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
									Last UOM sync
								</Text>
								<Text fontWeight="semibold" color="gray.800" fontSize="sm">
									{formatWhen(lastUomSync?.finished_at || lastUomSync?.started_at)}
								</Text>
								{lastUomSync?.status && (
									<Badge mt={1} colorScheme={statusColor(lastUomSync.status)} variant="subtle" borderRadius="full">
										{lastUomSync.status}
									</Badge>
								)}
							</CardBody>
						</Card>
					</SimpleGrid>

					<Card borderRadius="2xl" shadow="sm" overflow="hidden">
						{loading ? (
							<Center py={16}>
								<Spinner size="xl" thickness="4px" color="blue.500" />
							</Center>
						) : runs.length === 0 ? (
							<Center py={16}>
								<Box textAlign="center">
									<Icon as={MdSync} boxSize={10} color="gray.300" mb={3} />
									<Text color="gray.400">No sync runs yet.</Text>
									<Text fontSize="sm" color="gray.400" mt={1}>
										Run “Sync inventory” or enable scheduled sync.
									</Text>
								</Box>
							</Center>
						) : (
							<Box overflowX="auto">
								<Table variant="simple" size="sm">
									<Thead>
										<Tr bg="gray.50">
											<Th fontSize="xs" color="gray.500">Started</Th>
											<Th fontSize="xs" color="gray.500">Finished</Th>
											<Th fontSize="xs" color="gray.500">Type</Th>
											<Th fontSize="xs" color="gray.500">Status</Th>
											<Th fontSize="xs" color="gray.500" isNumeric>Items</Th>
											<Th fontSize="xs" color="gray.500">Error</Th>
										</Tr>
									</Thead>
									<Tbody>
										{runs.map((run) => (
											<Tr key={run.id} _hover={{ bg: 'gray.50' }}>
												<Td whiteSpace="nowrap">
													<Text fontSize="xs" color="gray.500">{formatWhen(run.started_at)}</Text>
												</Td>
												<Td whiteSpace="nowrap">
													<Text fontSize="xs" color="gray.500">{formatWhen(run.finished_at)}</Text>
												</Td>
												<Td>
													<Text fontSize="sm" color="gray.700" textTransform="capitalize">
														{run.sync_type || '—'}
													</Text>
												</Td>
												<Td>
													<Badge
														colorScheme={statusColor(run.status)}
														variant="subtle"
														borderRadius="full"
														px={2}
														py={0.5}
														fontSize="xs"
														textTransform="capitalize"
													>
														{run.status || '—'}
													</Badge>
												</Td>
												<Td isNumeric>
													<Text fontSize="sm" color="gray.700">{run.items_updated ?? 0}</Text>
												</Td>
												<Td maxW="280px">
													<Text fontSize="xs" color="red.500" noOfLines={2}>
														{run.error_message || '—'}
													</Text>
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</Box>
						)}

						{totalPages > 1 && (
							<Flex justify="space-between" align="center" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
								<Text fontSize="sm" color="gray.500">
									Page {currentPage} of {totalPages}
								</Text>
								<HStack spacing={2}>
									<Button
										size="sm"
										variant="ghost"
										leftIcon={<Icon as={MdChevronLeft} />}
										isDisabled={offset === 0}
										onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
									>
										Prev
									</Button>
									<Button
										size="sm"
										variant="ghost"
										rightIcon={<Icon as={MdChevronRight} />}
										isDisabled={currentPage >= totalPages}
										onClick={() => setOffset(offset + PAGE_SIZE)}
									>
										Next
									</Button>
								</HStack>
							</Flex>
						)}
					</Card>
				</Container>
			</Box>
		</>
	);
};

export default Fishbowl;
